import ResultView, {useResultView} from '@/components/ResultView';
import {visitSeed} from '@/lib/visitSeed';
import ResultOrder from "@/components/ResultOrder";
import { activeAtlasFilters } from '@/lib/atlasScope';
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { ExploreMap } from "@/components/ExploreMap";
import { FarmCard } from "@/components/FarmCard";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import AtlasFilters from "@/components/AtlasFilters";

const ALL = "__all__";
const FILTER_KEYS = [
  'country', 'region', 'municipality', 'reference_crop_id', 'icc_code',
  'reference_domain', 'animal_species_id', 'crop', 'subject_type', 'activity',
  'opportunity', 'species_id', 'variety', 'cultivar_id', 'cultivar_name_key', 'practice',
];
const readFilters = params => Object.fromEntries(FILTER_KEYS.map(key => [key, params.get(key) || '']));

export default function Explore() {
  const { t, tv, lang } = useLang();
  const [params, setParams] = useSearchParams();
  const focusTarget = params.get('focus');
  useEffect(() => {
    if (focusTarget === 'search') {
      const timer = setTimeout(() => { const input = document.querySelector('[data-testid="explore-search-input"]'); input?.scrollIntoView({block:'center'}); input?.focus(); }, 100);
      return () => clearTimeout(timer);
    }
  }, [focusTarget]);
  // The URL is the source of truth, including browser back/forward navigation.
  const search = params.get('search') || '';
  const sel = useMemo(() => readFilters(params), [params]);
  const order = ['random', 'recent', 'name'].includes(params.get('order')) ? params.get('order') : 'random';
  const requestedPage = Number(params.get('page') || 1);
  const page = Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const setSearch = value => setParams(previous => {
    const next = new URLSearchParams(previous);
    if (value) next.set('search', value); else next.delete('search');
    next.delete('page');
    return next;
  }, { replace: true });
  const setSel = update => setParams(previous => {
    const next = new URLSearchParams(previous);
    const selection = typeof update === 'function' ? update(readFilters(previous)) : update;
    FILTER_KEYS.forEach(key => { if (selection[key]) next.set(key, selection[key]); else next.delete(key); });
    next.delete('page');
    return next;
  });
  const setOrder = value => setParams(previous => {
    const next = new URLSearchParams(previous);
    if (value === 'random') next.delete('order'); else next.set('order', value);
    next.delete('page');
    return next;
  });
  const setPage = useCallback((value, replace = false) => {
    if (value === page) return;
    setParams(previous => {
      const next = new URLSearchParams(previous);
      if (value === 1) next.delete('page'); else next.set('page', String(value));
      return next;
    }, { replace });
  }, [page, setParams]);
  const [filters, setFilters] = useState({ countries: [], regions: [], crops: [], varieties: [], practices: [] });
  const [view,setView]=useResultView("compact");
  const [meta, setMeta] = useState({total:0, pages:1});
  const [seed] = useState(visitSeed);
  const [farms, setFarms] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const hasRequested = useRef(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(false);
  const [filtersAttempt, setFiltersAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setFiltersLoading(true); setFiltersError(false);
    api.get('/filters', { signal: controller.signal })
      .then(r => { if (!controller.signal.aborted) setFilters(r.data); })
      .catch(() => { if (!controller.signal.aborted) setFiltersError(true); })
      .finally(() => { if (!controller.signal.aborted) setFiltersLoading(false); });
    return () => controller.abort();
  }, [filtersAttempt]);

  const fetchFarms = useCallback((signal) => {
    hasRequested.current = true;
    const q = {};
    if (search) q.search = search;
    Object.entries(sel).forEach(([k, v]) => {
      if (v) q[k] = v;
    });
    api
      .get("/farms", { params: { ...activeAtlasFilters(q), paginated:true, summary:true, page_size:24, order, seed, page }, signal })
      .then((r) => { if (!signal.aborted) { setFarms(r.data.items); setMeta({total:r.data.total, pages:r.data.pages}); setPage(r.data.page, true); } })
      .catch(() => { if (!signal.aborted) setLoadError(true); })
      .finally(() => { if (!signal.aborted) setLoading(false); });
  }, [search, sel, order, seed, page, setPage]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setLoadError(false); setFarms([]);
    const id = setTimeout(() => fetchFarms(controller.signal), hasRequested.current ? 250 : 0);
    return () => { clearTimeout(id); controller.abort(); };
  }, [fetchFarms, attempt]);

  const setFilter = (key, value) =>
    setSel((s) => {
      const next = { ...s, [key]: value === ALL ? "" : value };
      if (key === "crop") { next.species_id = ""; next.variety = ""; next.cultivar_id = ""; next.cultivar_name_key = ""; }
      if (key === "species_id") { next.variety = ""; next.cultivar_id = ""; next.cultivar_name_key = ""; }
      if (key === "variety") { next.cultivar_id = ""; next.cultivar_name_key = ""; }
      if (key === "cultivar_id") { next.variety = ""; next.cultivar_name_key = ""; }
      if (key === "country") { next.region = ""; next.municipality = ""; } // region depends on country
      return next;
    });

  const clearAll = () => setParams(previous => {
    const next = new URLSearchParams(previous);
    [...FILTER_KEYS, 'search', 'page'].forEach(key => next.delete(key));
    return next;
  });

  const activeCount = Object.values(sel).filter(Boolean).length + (search ? 1 : 0);

  const FilterSelect = ({ field, placeholder, options }) => (
    <Select value={sel[field] || ALL} onValueChange={(v) => setFilter(field, v)}>
      <SelectTrigger
        data-testid={`filter-${field}`}
        aria-label={t(`explore.${field}`)}
        className="h-10 rounded-full border-[#E2DACB] bg-[#FFFDF8] text-sm font-sans min-w-[130px]"
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="z-[1100]">
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {tv(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const PracticeSelect = () => {
    const cats = filters.practices_by_category || {};
    const order = ["water_management", "water_source", "soil", "biodiversity", "cropping_system"];
    return (
      <Select value={sel.practice || ALL} onValueChange={(v) => setFilter("practice", v)}>
        <SelectTrigger
          data-testid="filter-practice"
          aria-label={t("explore.practicesFeatures")}
          className="h-10 rounded-full border-[#E2DACB] bg-[#FFFDF8] text-sm font-sans min-w-[130px]"
        >
          <SelectValue placeholder={t("explore.practicesFeatures")} />
        </SelectTrigger>
        <SelectContent className="z-[1100]">
          <SelectItem value={ALL}>{t("explore.practicesFeatures")}</SelectItem>
          {order.filter((c) => (cats[c] || []).length).map((c) => (
            <SelectGroup key={c}>
              <SelectLabel className="text-[#C46A3A] text-xs uppercase tracking-wider">
                {t(`explore.practiceCats.${c}`)}
              </SelectLabel>
              {cats[c].map((o) => (
                <SelectItem key={o} value={o}>{tv(o, c)}</SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div data-testid="explore-page" className="grain-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-5">
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#2A2521]">
          {t("explore.title")}
        </h1>
        <p className="font-sans text-[#6E6258] mt-1.5">{t("explore.subtitle")}</p>

        {/* Filter bar */}
        <div className="mt-6 flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1 max-w-xl">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A8F80]" />
            <input
              data-testid="explore-search-input"
              aria-label={t("explore.searchLabel")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("explore.searchPlaceholder")}
              className="w-full h-11 pl-11 pr-4 rounded-full bg-[#FFFDF8] border border-[#E2DACB] text-sm font-sans focus:outline-none focus:ring-2 focus:ring-[#557448]"
            />
          </div>
          <div className="flex flex-wrap gap-2.5 items-center">
            {!["livestock", "ornamental", "forestry", "mushrooms", "green_spaces", "truffles"].includes(sel.activity) && <FilterSelect field="crop" placeholder={t("explore.allCrops")} options={filters.crops} />}
            <Button
              variant="outline"
              data-testid="toggle-more-filters"
              aria-expanded={showMore}
              aria-controls={showMore ? "explore-more-filters" : undefined}
              onClick={() => setShowMore((v) => !v)}
              className="h-10 rounded-full border-[#E2DACB] bg-[#FFFDF8] text-sm font-sans gap-2 text-[#4A443D]"
            >
              <SlidersHorizontal size={15} /> {t("explore.moreFilters")}
            </Button>
            {activeCount > 0 && (
              <button
                data-testid="clear-filters"
                onClick={clearAll}
                className="h-10 inline-flex items-center gap-1 text-sm font-sans text-[#557448] hover:underline px-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4"
              >
                <X size={15} /> {t("explore.clear")}
              </button>
            )}
          </div>
        </div>
        {filtersLoading && <p role="status" className="mt-3 text-sm text-[#6E6258]">{t("explore.filtersLoading")}</p>}
        {filtersError && <p role="alert" data-testid="explore-filters-error" className="mt-3 text-sm text-[#6E6258]">{t("explore.filtersError")} <button type="button" className="underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4" onClick={() => setFiltersAttempt(a => a + 1)}>{t("content.retry")}</button></p>}
        {showMore && (
          <div className="mt-3 flex flex-wrap gap-2.5" data-testid="more-filters-row" id="explore-more-filters">
            {sel.activity !== "livestock" && <Select value={sel.cultivar_id ? `cultivar:${sel.cultivar_id}` : sel.variety ? `legacy:${sel.variety}` : ALL}
              onValueChange={(value) => {
                if (value === ALL) {
                  setSel((prev) => ({ ...prev, cultivar_id: "", cultivar_name_key: "", variety: "" }));
                } else if (value.startsWith("cultivar:")) {
                  setSel((prev) => ({ ...prev, cultivar_id: value.slice(9), cultivar_name_key: "", variety: "" }));
                } else {
                  setSel((prev) => ({ ...prev, cultivar_id: "", variety: value.slice(7) }));
                }
              }}>
              <SelectTrigger aria-label={t("explore.variety")} data-testid="filter-variety" className="h-10 rounded-full border-[#E2DACB] bg-[#FFFDF8] text-sm font-sans min-w-[150px]">
                <SelectValue placeholder={t("explore.allVarieties")} />
              </SelectTrigger>
              <SelectContent className="z-[1100]">
                <SelectItem value={ALL}>{t("explore.allVarieties")}</SelectItem>
                {(sel.crop ? filters.varieties_by_crop?.[sel.crop] || [] : filters.varieties || []).map((name) => (
                  <SelectItem key={name} value={`legacy:${name}`}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            }<PracticeSelect />
          </div>
        )}
        <AtlasFilters value={sel} onChange={setSel} />
      </div>

      {/* Map + list */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <ResultOrder order={order} onOrder={setOrder} page={page} pages={meta.pages} total={meta.total} onPage={setPage} loading={loading} error={loadError} />
        <ResultView value={view} onChange={setView}/>
        <p className="text-sm text-[#6E6258] mb-3">{lang === "it" ? "La mappa mostra i soggetti della pagina corrente (fino a 24)." : "The map shows subjects on the current page (up to 24)."}</p>
        <div className="flex flex-col lg:flex-row gap-5 lg:h-[calc(100vh-150px)]">
          <div className="lg:w-[56%] h-[46vh] lg:h-full rounded-md overflow-hidden border border-[#E2DACB] relative">
            <ExploreMap farms={farms} focusId={params.get("focus")} />
            <div className="absolute bottom-3 left-3 z-[400] bg-[#FFFDF8]/95 backdrop-blur px-3 py-1.5 rounded-full border border-[#E2DACB] text-xs font-sans font-medium text-[#4A443D]">
              <span data-testid="results-count">{loading || loadError ? "—" : meta.total}</span> {t("explore.resultsFound")}
            </div>
          </div>
          <div className="lg:w-[44%] lg:h-full lg:overflow-y-auto pa-scroll lg:pr-1">
            {loadError ? <div role="alert" className="p-6">{t("content.catalogError")}<button className="block mt-3 underline" onClick={() => setAttempt(a => a + 1)}>{t("content.retry")}</button></div> : loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-72 rounded-md bg-[#EFE9DA] animate-pulse" />
                ))}
              </div>
            ) : farms.length === 0 ? (
              <div className="py-16 text-center font-sans text-[#6E6258]" data-testid="no-results">
                {t("explore.noResults")}
              </div>
            ) : (
              <div className={view==="compact"?"grid grid-cols-1 gap-3":"grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-4"}>
                {farms.map((f) => (
                  <FarmCard key={f.id} farm={f} compact={view==="compact"} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



